'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  concepto?: string;
  copyDate: string;
  setCopyDate: (v: string) => void;
  copyBase: string;
  setCopyBase: (v: string) => void;
  copyIva: string;
  setCopyIva: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ExpenseCopyDialog({
  open,
  concepto,
  copyDate,
  setCopyDate,
  copyBase,
  setCopyBase,
  copyIva,
  setCopyIva,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {t('expenseCopyDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
              {t('expenseCopyDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          {concepto && (
            <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
              {t('expenseCopyDialog.concept')}:{' '}
              <span className="font-medium text-gray-800 dark:text-white">
                {concepto}
              </span>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4">
            <div className="space-y-1">
              <Label className="text-sm text-gray-700 dark:text-gray-300">
                {t('expenseCopyDialog.labelDate')}
              </Label>
              <Input
                type="date"
                value={copyDate}
                onChange={(e) => setCopyDate(e.target.value)}
                className="h-10 text-sm rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm text-gray-700 dark:text-gray-300">
                  {t('expenseCopyDialog.labelBase')}
                </Label>
                <Input
                  value={copyBase}
                  onChange={(e) =>
                    setCopyBase(e.target.value.replace(',', '.'))
                  }
                  className="rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-sm text-gray-700 dark:text-gray-300">
                  {t('expenseCopyDialog.labelIva')}
                </Label>
                <Input
                  value={copyIva}
                  onChange={(e) =>
                    setCopyIva(e.target.value.replace(',', '.'))
                  }
                  className="rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onCancel}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('expenseCopyDialog.cancel')}
          </Button>

          <Button
            onClick={onConfirm}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {t('expenseCopyDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}